import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Phone } from "lucide-react";
import { EMAIL, PHONE, PHONE_HREF } from "@/components/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-white/25 bg-transparent px-4 py-4 text-base text-white placeholder-white/35 transition-colors duration-200 focus:border-royal";

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", phone: "", town: "", description: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/quotes`, form);
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong sending that. Please call us instead — " + PHONE);
    }
  };

  return (
    <section id="quote" className="grain relative bg-navy" data-testid="quote-section">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="px-5 py-20 sm:px-10 lg:col-span-5 lg:py-28 xl:px-16">
          <p className="mb-3 font-mono2 text-[11px] uppercase tracking-[0.35em] text-royal">Free quote</p>
          <h2 className="font-display text-5xl uppercase leading-none text-white sm:text-7xl">
            Get it gone.
          </h2>
          <p className="mt-6 max-w-sm text-base text-white/70 md:text-lg">
            Tell us what needs to go and where you are. We'll call you back with a price —
            or skip the form and call right now.
          </p>
          <a
            href={PHONE_HREF}
            data-testid="quote-call-button"
            className="btn-hard btn-hard-light mt-10 inline-flex items-center gap-3 bg-royal px-8 py-5 font-display text-2xl tracking-wide text-white"
          >
            <Phone className="h-6 w-6" strokeWidth={2.5} />
            {PHONE}
          </a>
          <p className="mt-6 font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50">
            or email {EMAIL}
          </p>
        </div>
        <div className="border-t border-white/15 px-5 py-20 sm:px-10 lg:col-span-7 lg:border-l lg:border-t-0 lg:py-28 xl:px-16">
          {status === "done" ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex h-full flex-col items-start justify-center border border-white/25 p-10"
              data-testid="quote-form-success"
            >
              <CheckCircle2 className="h-10 w-10 text-royal" strokeWidth={2} />
              <h3 className="mt-6 font-display text-4xl uppercase text-white">Got it.</h3>
              <p className="mt-3 max-w-sm text-white/70">
                Your quote request is in. We'll call you back shortly. Need it faster?
                Call <a href={PHONE_HREF} className="underline">{PHONE}</a>.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="quote-form">
              <label className="block">
                <span className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/60">Name</span>
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  data-testid="quote-form-name-input"
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/60">Phone</span>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Best number to call"
                  data-testid="quote-form-phone-input"
                  className={inputCls}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/60">Town</span>
                <input
                  required
                  value={form.town}
                  onChange={set("town")}
                  placeholder="Town in Mass or Rhode Island"
                  data-testid="quote-form-town-input"
                  className={inputCls}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/60">What needs to go?</span>
                <textarea
                  required
                  rows={5}
                  value={form.description}
                  onChange={set("description")}
                  placeholder="e.g. Garage cleanout — old furniture, boxes, a fridge"
                  data-testid="quote-form-details-input"
                  className={inputCls}
                />
              </label>
              {error && (
                <p className="text-sm text-red-400 sm:col-span-2" data-testid="quote-form-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="quote-form-submit-button"
                className="btn-hard btn-hard-light bg-white px-8 py-5 font-display text-xl tracking-wide text-navy disabled:opacity-60 sm:col-span-2"
              >
                {status === "sending" ? "SENDING..." : "SEND MY FREE QUOTE REQUEST"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
